import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProductForm from "./ProductForm";
import AddProductImages from "./AddPRoductImg";
import ProductVariantForm from "./OptionalForm";
import { getProductBySlug, editProduct } from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";

export default function EditProductStepper() {
  const location = useLocation();
  const navigate = useNavigate();

  const productId = location?.state?.productId;
  const slug = location?.state?.slug;

  const storageKey = `editProductStep_${productId}`;

  const [step, setStep] = useState(() => {
    return Number(localStorage.getItem(storageKey)) || 1;
  });

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [success, setSuccess] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});

  // ================= SAVE STEP =================
  useEffect(() => {
    if (productId) {
      localStorage.setItem(storageKey, step);
    }
  }, [step, storageKey, productId]);

  // ================= FETCH PRODUCT =================
  useEffect(() => {
    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);

      const response = await getProductBySlug(slug);
      setProduct(response?.data);
    } catch (error) {
      console.log("FETCH PRODUCT ERROR:", error);
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // ================= UPDATE PRODUCT =================
  const handleUpdateProduct = async (formData) => {
  try {
    setLoading(true);
    setBackendErrors({});
    const payload = {
      ...formData,

      coupons: Array.isArray(formData.coupons)
        ? formData.coupons.map(String)
        : formData.coupons
          ? [String(formData.coupons)]
          : [],

      status: formData.status,
    };

    const response = await editProduct(productId, payload);
    console.log("edit:",response);
    if (response?.success === false || response?.status === 422) {
      setBackendErrors(response?.error || {});
      return;
    }

    // Reload latest product data
    await fetchProduct();

    setStep(2);
  } catch (error) {
    if (error?.response?.status === 422) {
      setBackendErrors(error.response.data?.error || {});
      return;
    }

    alert(getErrorMessage(error));
  } finally {
    setLoading(false);
  }
};

  // ================= IMAGE SUCCESS =================
  const handleImagesSuccess = () => {
    setStep(3);
  };

  // ================= VARIANT SUCCESS =================
  const handleVariantSuccess = () => {
    setSuccess(true);

    localStorage.removeItem(storageKey);

    setTimeout(() => {
      navigate("/products");
    }, 1500);
  };

  // ================= LOADING =================
  if (loading && !product) {
    return (
      <div className="container py-5 text-center">
        <h5>Loading Product...</h5>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="card shadow border-0">
        <div className="card-body">
          <h3 className="mb-4">Edit Product</h3>

          {/* STEPPER */}
          <div className="d-flex justify-content-between align-items-center mb-4 position-relative">
            <div
              style={{
                position: "absolute",
                top: "22px",
                left: "8%",
                width: "82%",
                height: "4px",
                background: "#dee2e6",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: step === 1 ? "0%" : step === 2 ? "50%" : "100%",
                  height: "100%",
                  background: "#59b71f",
                  transition: "0.3s",
                }}
              />
            </div>

            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="text-center position-relative"
                style={{ zIndex: 2 }}
              >
                <div
                  style={{
                    width: "45px",
                    height: "45px",
                    borderRadius: "50%",
                    background: step >= item ? "#59b71f" : "#dee2e6",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "18px",
                    margin: "0 auto",
                  }}
                >
                  {item}
                </div>

                <p className="mt-2 mb-0">
                  {item === 1
                    ? "Product Details"
                    : item === 2
                      ? "Upload Images"
                      : "Product Variations"}
                </p>
              </div>
            ))}
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h5 className="mb-3">Edit Product Details</h5>

              <ProductForm
                initialData={product}
                isEdit={true}
                onSubmit={handleUpdateProduct}
                loading={loading}
                backendErrors={backendErrors}
              />
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <h5 className="mb-3">Update Product Images</h5>

              <AddProductImages
                id={productId}
                existingImages={product?.imageUrls || []}
                existingFeaturedImage={product?.featuredImageUrl || ""}
                onSuccess={handleImagesSuccess}
              />
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && !success && (
            <>
              <h5 className="mb-3">Update Product Variations</h5>

              <ProductVariantForm
                id={productId}
                initialVariants={product?.variants || []}
                isEdit={true}
                onSuccess={handleVariantSuccess}
              />
            </>
          )}

          {/* SUCCESS */}
          {success && (
            <div className="alert alert-success text-center mt-4" role="alert">
              Product updated successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
